import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { User, FileText, Star, Upload, Trash2, CheckCircle, AlertCircle, Eye, Download, Key, UserX } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import BragSheet from '../components/BragSheet';
import DreamSheet from '../components/DreamSheet';
import QualityWarning from '../components/QualityWarning';
import ExtractionErrorMessage from '../components/ExtractionErrorMessage';
import ExtractedTextPreviewModal from '../components/ExtractedTextPreviewModal';
import { extractTextFromPDF, validatePDFFile, quickPDFTextCheck } from '../utils/pdfExtractor';
import { cleanExtractedText, getCleanTextPreview } from '../utils/textCleaner';
import {
  startExtractionWorker,
  pollExtractionJob,
  getErrorRemediation,
  getConfidenceMessage,
  formatProcessingTime,
  ExtractionJob
} from '../utils/extractionJobHelpers';
import { extractWithPdfCo } from '../utils/externalExtraction';
import { draftStorage, formatDraftAge } from '../utils/draftStorage';

interface Profile {
  id: string;
  full_name: string | null;
  years_experience: number | null;
  preferred_role: string | null;
  preferred_industry: string | null;
  primary_motivation: 'career_growth' | 'currently_displaced' | 'second_act' | null;
  current_job: string | null;
  current_industry: string | null;
  total_compensation_range: string | null;
  preferred_location: string | null;
  preferred_work_environment: 'in_office' | 'hybrid' | 'remote' | null;
  created_at: string;
  updated_at: string;
}

interface Document {
  id: string;
  user_id: string;
  name: string;
  type: 'resume';
  file_path: string;
  extracted_content: string | null;
  extraction_method: string | null;
  text_extraction_status: string | null;
  extraction_error_message: string | null;
  extraction_metadata: any;
  extracted_at: string | null;
  status: 'uploaded' | 'processing' | 'processed' | 'error';
  quality_score: number | null;
  confidence_level: 'high' | 'medium' | 'low' | null;
  quality_issues: any;
  quality_check_passed: boolean | null;
  quality_checked_at: string | null;
  minimum_quality_threshold: number | null;
  ocr_used: boolean | null;
  ocr_confidence: number | null;
  is_image_based: boolean | null;
  created_at: string;
  updated_at: string;
}

const MyProfile: React.FC = () => {
  const { user, repairUserProfile } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [extractionStatus, setExtractionStatus] = useState<{ [key: string]: string }>({});
  const [extractionJob, setExtractionJob] = useState<{ [key: string]: ExtractionJob | null }>({});
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    document: Document | null;
  }>({ isOpen: false, document: null });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [showDraftRestored, setShowDraftRestored] = useState(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedProfileRef = useRef(false);
  const isMountedRef = useRef(true);

  // Form state - Declared before formDataRef to avoid initialization error
  const [formData, setFormData] = useState({
    full_name: '',
    years_experience: '',
    preferred_role: '',
    preferred_industry: '',
    primary_motivation: '',
    current_job: '',
    current_industry: '',
    total_compensation_range: '',
    preferred_location: '',
    preferred_work_environment: ''
  });

  // Ref to track current form data for auto-save (declared after formData)
  const formDataRef = useRef(formData);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const saveFormDraft = useCallback(() => {
    if (!user?.id) return;
    draftStorage.save('profile_form', formDataRef.current, user.id);
  }, [user?.id]);

  const loadFormDraft = useCallback(() => {
    if (!user?.id) return null;
    return draftStorage.load('profile_form', user.id);
  }, [user?.id]);

  const clearFormDraft = useCallback(() => {
    if (!user?.id) return;
    draftStorage.remove('profile_form', user.id);
    setShowDraftRestored(false);
  }, [user?.id]);

  const autoSave = useCallback(async () => {
    if (!user || !profile || !hasUnsavedChanges || isSaving) return;

    setIsAutoSaving(true);
    console.log('🔄 MyProfile: Auto-saving profile...');

    try {
      const updateData = {
        full_name: formDataRef.current.full_name.trim() || null,
        years_experience: formDataRef.current.years_experience ? parseInt(formDataRef.current.years_experience) : null,
        preferred_role: formDataRef.current.preferred_role.trim() || null,
        preferred_industry: formDataRef.current.preferred_industry.trim() || null,
        primary_motivation: formDataRef.current.primary_motivation || null,
        current_job: formDataRef.current.current_job.trim() || null,
        current_industry: formDataRef.current.current_industry.trim() || null,
        total_compensation_range: formDataRef.current.total_compensation_range || null,
        preferred_location: formDataRef.current.preferred_location.trim() || null,
        preferred_work_environment: formDataRef.current.preferred_work_environment || null
      };

      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select();

      if (error) throw error;

      if (data && data[0]) {
        setProfile(data[0]);
        setLastSavedAt(new Date());
        setHasUnsavedChanges(false);
        clearFormDraft();
        console.log('✅ MyProfile: Auto-save successful');
      }
    } catch (error: any) {
      console.error('❌ MyProfile: Auto-save failed:', error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [user, profile, hasUnsavedChanges, isSaving, clearFormDraft]);

  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔧 MyProfile: Component mounted, user:', user?.email);

    return () => {
      isMountedRef.current = false;
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      console.log('🔧 MyProfile: Component unmounted');
    };
  }, []);

  useEffect(() => {
    const initializeProfile = async () => {
      if (!user) {
        console.log('⚠️ MyProfile: No user found, redirecting...');
        setIsLoading(false);
        return;
      }

      console.log('🚀 MyProfile: Initializing profile for user:', user.email);

      loadingTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && isLoading) {
          console.warn('⏰ MyProfile: Loading timeout reached');
          setLoadingTimeout(true);
          setIsLoading(false);
          setError('Loading is taking longer than expected. Please try refreshing the page.');
        }
      }, 10000);

      try {
        await fetchProfile();
        await fetchDocuments();
      } catch (err: any) {
        console.error('❌ MyProfile: Initialization error:', err);
        if (isMountedRef.current) {
          setError(err.message || 'Failed to load profile data');
          setIsLoading(false);
        }
      }
    };

    initializeProfile();
  }, [user]);

  useEffect(() => {
    if (profile && !hasLoadedProfileRef.current) {
      const draft = loadFormDraft();

      if (draft && user?.id) {
        const draftAge = draftStorage.getDraftAge('profile_form', user.id);
        if (draftAge && draftAge < 7 * 24 * 60 * 60 * 1000) {
          setFormData(draft);
          setHasUnsavedChanges(true);
          setShowDraftRestored(true);
          console.log('📝 MyProfile: Restored draft from', formatDraftAge(draftAge));
          toast.info(`Draft restored from ${formatDraftAge(draftAge)}. Click Save to keep changes or continue editing.`);
          hasLoadedProfileRef.current = true;
          return;
        }
      }

      setFormData({
        full_name: profile.full_name || '',
        years_experience: profile.years_experience?.toString() || '',
        preferred_role: profile.preferred_role || '',
        preferred_industry: profile.preferred_industry || '',
        primary_motivation: profile.primary_motivation || '',
        current_job: profile.current_job || '',
        current_industry: profile.current_industry || '',
        total_compensation_range: profile.total_compensation_range || '',
        preferred_location: profile.preferred_location || '',
        preferred_work_environment: profile.preferred_work_environment || ''
      });
      hasLoadedProfileRef.current = true;
    }
  }, [profile, loadFormDraft, user?.id]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      saveFormDraft();

      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      autoSaveTimerRef.current = setTimeout(() => {
        autoSave();
      }, 30000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, autoSave, saveFormDraft]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hasUnsavedChanges) {
        console.log('📱 MyProfile: Tab hidden, saving draft...');
        saveFormDraft();
        if (user && profile) {
          autoSave();
        }
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        saveFormDraft();
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, saveFormDraft, autoSave, user, profile]);

  const handleFormChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  }, []);

  const fetchProfile = async () => {
    if (!user) {
      console.warn('⚠️ MyProfile: Cannot fetch profile - no user');
      return;
    }

    try {
      console.log('📊 MyProfile: Fetching profile for user:', user.id);

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('❌ MyProfile: Profile fetch error:', error);
        throw error;
      }

      if (!data) {
        console.warn('⚠️ MyProfile: Profile not found, attempting to create...');
        if (isMountedRef.current) {
          setIsInitializing(true);
          toast.info('Setting up your profile...');
        }

        const { data: createResult, error: createError } = await supabase.rpc('ensure_user_profile_exists', {
          user_id: user.id,
          user_email: user.email || null,
          full_name: user.user_metadata?.full_name || user.email || 'User'
        });

        if (createError) {
          console.error('❌ MyProfile: Profile creation error:', createError);
          throw new Error('Failed to create profile. Please contact support.');
        }

        const { data: newProfile, error: refetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (refetchError || !newProfile) {
          throw new Error('Profile created but could not be retrieved.');
        }

        console.log('✅ MyProfile: Profile created and fetched successfully');
        if (isMountedRef.current) {
          setProfile(newProfile);
          setIsInitializing(false);
          toast.success('Profile created successfully!');
        }
        return;
      }

      console.log('✅ MyProfile: Profile fetched successfully:', data);
      if (isMountedRef.current) {
        setProfile(data);
        setError(null);
      }
    } catch (error: any) {
      console.error('❌ MyProfile: Error fetching profile:', error);
      if (isMountedRef.current) {
        setError(error.message || 'Failed to load profile');
        toast.error('Failed to load profile: ' + (error.message || 'Unknown error'));
        setIsInitializing(false);
      }
      throw error;
    }
  };

  const fetchDocuments = async () => {
    if (!user) {
      console.warn('⚠️ MyProfile: Cannot fetch documents - no user');
      return;
    }

    try {
      console.log('📊 MyProfile: Fetching documents for user:', user.id);

      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ MyProfile: Documents fetch error:', error);
        if (error.message.includes('permission') || error.message.includes('RLS')) {
          console.warn('⚠️ MyProfile: RLS policy issue detected');
          if (isMountedRef.current) {
            setDocuments([]);
          }
          return;
        }
        throw error;
      }

      console.log('✅ MyProfile: Documents fetched successfully:', data?.length || 0, 'documents');
      if (isMountedRef.current) {
        setDocuments(data || []);
      }
    } catch (error: any) {
      console.error('❌ MyProfile: Error fetching documents:', error);
      if (isMountedRef.current) {
        setDocuments([]);
        console.warn('⚠️ MyProfile: Documents fetch failed, but continuing with empty array');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      }
    }
  };

  const handleProfileSave = async () => {
    if (!user || !profile) return;

    setIsSaving(true);
    console.log('💾 MyProfile: Starting profile save process...');

    try {
      // Prepare update data with proper type conversion
      const updateData = {
        full_name: formData.full_name.trim() || null,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        preferred_role: formData.preferred_role.trim() || null,
        preferred_industry: formData.preferred_industry.trim() || null,
        primary_motivation: formData.primary_motivation || null,
        current_job: formData.current_job.trim() || null,
        current_industry: formData.current_industry.trim() || null,
        total_compensation_range: formData.total_compensation_range || null,
        preferred_location: formData.preferred_location.trim() || null,
        preferred_work_environment: formData.preferred_work_environment || null
      };

      console.log('📝 MyProfile: Update data to send:', updateData);
      console.log('📡 MyProfile: Executing UPDATE query...');

      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select();

      console.log('📡 MyProfile: UPDATE response:', { data, error, success: !error });

      if (error) {
        console.error('❌ MyProfile: UPDATE failed:', error);
        console.error('❌ MyProfile: Full UPDATE error object:', JSON.stringify(error, null, 2));
        throw new Error(`Failed to update profile: ${error.message}`);
      }

      console.log('✅ MyProfile: Profile updated successfully:', data);
      
      // Update local state
      if (data && data[0]) {
        setProfile(data[0]);
        setLastSavedAt(new Date());
        setHasUnsavedChanges(false);
        clearFormDraft();
        console.log('✅ MyProfile: Local profile state updated');
      }

      toast.success('Profile saved successfully!');
    } catch (error: any) {
      console.error('❌ MyProfile: CATCH BLOCK - Error updating profile:', error);
      console.error('❌ MyProfile: Full error object:', JSON.stringify(error, null, 2));
      
      // Check for specific schema cache issues
      if (error.message?.includes('Could not find') && error.message?.includes('column')) {
        console.log('🔧 MyProfile: Schema cache issue detected, attempting fix...');
        
        try {
          const { data: fixResult, error: fixError } = await supabase.functions.invoke('fix-schema-issues');
          
          if (fixError) {
            console.error('❌ MyProfile: Schema fix failed:', fixError);
            toast.error('Schema fix failed. Please try refreshing the page.');
          } else {
            console.log('✅ MyProfile: Schema fix successful:', fixResult);
            toast.info('Schema cache refreshed. Please try saving again.');
          }
        } catch (fixException) {
          console.error('❌ MyProfile: Schema fix exception:', fixException);
          toast.error('Unable to fix schema cache. Please refresh the page.');
        }
      } else {
        toast.error(error.message || 'Failed to update profile');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type "DELETE" to confirm account deletion');
      return;
    }

    setIsDeletingAccount(true);

    try {
      console.log('🗑️ Initiating account deletion process...');
      
      const { data, error } = await supabase.functions.invoke('delete-user-account', {
        body: {
          userId: user?.id,
          confirmationText: deleteConfirmation
        }
      });

      if (error) {
        throw new Error(`Account deletion failed: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Account deletion failed');
      }

      console.log('✅ Account deletion completed:', data);
      
      toast.success('Your account has been deleted successfully. You will be redirected shortly.');
      
      // Close modal and sign out
      setShowDeleteAccount(false);
      setDeleteConfirmation('');
      
      // Sign out and redirect after a brief delay
      setTimeout(async () => {
        try {
          await supabase.auth.signOut();
          window.location.href = '/';
        } catch (signOutError) {
          console.error('Error signing out after deletion:', signOutError);
          window.location.href = '/';
        }
      }, 2000);
      
    } catch (error: any) {
      console.error('❌ Error deleting account:', error);
      
      if (error.message?.includes('confirmation text')) {
        toast.error('Please type "DELETE" exactly to confirm');
      } else {
        toast.error(error.message || 'Failed to delete account. Please contact support.');
      }
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleFileUpload = async (file: File, type: 'resume') => {
    if (!user) return;

    console.log(`📤 MyProfile: Starting ${type} upload:`, file.name, file.size, 'bytes');
    console.log(`🔍 MyProfile: File details:`, {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    // Quick pre-upload PDF text check
    console.log(`🔍 MyProfile: Performing quick PDF text check before upload...`);
    const textCheck = await quickPDFTextCheck(file);
    console.log(`📊 MyProfile: Quick check results:`, textCheck);

    if (!textCheck.hasText && textCheck.confidence === 'high') {
      toast.error(`⚠️ Warning: ${textCheck.message}\n\nThe file will be uploaded, but text extraction may not work. You can still proceed, but consider re-exporting your resume as a text-based PDF for best results.`);
    } else if (textCheck.confidence === 'medium') {
      console.log(`⚠️ MyProfile: Medium confidence on text extraction`);
    }

    // Validate file
    const validation = validatePDFFile(file);
    if (!validation.valid) {
      console.error(`❌ MyProfile: File validation failed for ${type}:`, validation.error);
      toast.error(validation.error || 'Invalid file');
      return;
    }

    console.log(`✅ MyProfile: File validation passed for ${type}`);
    setIsUploading(true);
    setUploadProgress({ [type]: 0 });

    try {
      // Check for existing document of this type
      const existingDoc = documents.find(doc => doc.type === type);
      if (existingDoc) {
        console.log(`📋 MyProfile: Found existing ${type} document:`, existingDoc.id);
        
        // Ask user if they want to replace
        const shouldReplace = confirm(`You already have a ${type} uploaded. Do you want to replace it with the new file?`);
        if (!shouldReplace) {
          console.log(`❌ MyProfile: User cancelled ${type} replacement`);
          setIsUploading(false);
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[type];
            return newProgress;
          });
          return;
        }
        
        console.log(`🔄 MyProfile: User confirmed ${type} replacement`);
      }

      // Generate unique file path
      const timestamp = Date.now();
      const fileName = `${type}_${timestamp}_${file.name}`;
      let filePath = `${user.id}/${type}/${fileName}`;

      console.log(`📁 MyProfile: Upload path: ${filePath}`);

      // If replacing existing document, delete old file from storage first
      if (existingDoc && existingDoc.file_path) {
        try {
          console.log(`🗑️ MyProfile: Deleting old ${type} file:`, existingDoc.file_path);
          const { error: deleteError } = await supabase.storage
            .from('documents')
            .remove([existingDoc.file_path]);
          
          if (deleteError) {
            console.warn(`⚠️ MyProfile: Failed to delete old ${type} file:`, deleteError.message);
            // Continue anyway - the old file will remain but won't be referenced
          } else {
            console.log(`✅ MyProfile: Successfully deleted old ${type} file`);
          }
        } catch (deleteException) {
          console.warn(`⚠️ MyProfile: Exception deleting old ${type} file:`, deleteException);
          // Continue anyway
        }
      }

      // Upload to Supabase Storage
      setUploadProgress({ [type]: 25 });
      console.log(`📤 MyProfile: Uploading ${type} to storage...`);
      console.log(`📤 MyProfile: Storage upload starting - bucket: documents, path: ${filePath}`);
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file, {
          contentType: 'application/pdf',
          upsert: true // Allow overwriting if file exists
        });

      if (uploadError) {
        console.error(`❌ MyProfile: ${type} upload error:`, uploadError);
        console.error(`❌ MyProfile: Full upload error details:`, {
          message: uploadError.message,
          statusCode: uploadError.statusCode,
          error: uploadError
        });
        
        // Handle specific upload errors
        if (uploadError.message.includes('The resource already exists')) {
          console.log(`🔄 MyProfile: File exists, trying with upsert...`);
          // Try again with a different filename
          const retryFileName = `${type}_retry_${timestamp}_${file.name}`;
          const retryFilePath = `${user.id}/${type}/${retryFileName}`;
          
          console.log(`🔄 MyProfile: Retry upload with path: ${retryFilePath}`);
          const { error: retryError } = await supabase.storage
            .from('documents')
            .upload(retryFilePath, file, {
              contentType: 'application/pdf',
              upsert: true
            });
          
          if (retryError) {
            console.error(`❌ MyProfile: Retry upload also failed:`, retryError);
            throw new Error(`Upload failed after retry: ${retryError.message}`);
          }
          
          // Update file path for the rest of the process
          filePath = retryFilePath;
          toast.success(`${type} uploaded and processed with server-side text extraction!`);
        } else {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }
      }

      console.log(`✅ MyProfile: ${type} uploaded to storage successfully`);
      setUploadProgress({ [type]: 50 });

      // Extract text from PDF
      console.log(`🔍 MyProfile: Extracting text from ${type} PDF...`);
      console.log(`🔍 MyProfile: About to call extractTextFromPDF function with file:`, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      
      const extractionResult = await extractTextFromPDF(file);
      
      console.log(`📊 MyProfile: PDF extraction result for ${type}:`, {
        success: extractionResult.success,
        textLength: extractionResult.text?.length || 0,
        method: extractionResult.extractionMethod,
        error: extractionResult.error
      });
      
      if (!extractionResult.success) {
        console.warn(`⚠️ MyProfile: ${type} text extraction failed:`, extractionResult.error);
        console.log(`📊 MyProfile: Will attempt server-side extraction via Edge Function`);
        // Don't show error yet - we'll try the Edge Function fallback
      }

      setUploadProgress({ [type]: 75 });

      // Clean extracted text if available
      let cleanedText = '';
      let extractionMethod = 'none';
      
      if (extractionResult.success && extractionResult.text) {
        console.log(`🧹 MyProfile: Cleaning extracted text for ${type}...`);
        const cleaningResult = cleanExtractedText(extractionResult.text);
        cleanedText = cleaningResult.cleanedText;
        extractionMethod = extractionResult.extractionMethod;

        console.log(`✅ MyProfile: ${type} text cleaned:`, {
          originalLength: cleaningResult.originalLength,
          cleanedLength: cleaningResult.cleanedLength,
          compressionRatio: cleaningResult.metadata.compressionRatio
        });
      } else {
        console.warn(`⚠️ MyProfile: No text to clean for ${type} - extraction was not successful`);
      }

      // Create or update document record in database
      console.log(`💾 MyProfile: Saving ${type} document record to database...`);
      let docData;
      let docError;
      
      if (existingDoc) {
        // Update existing document record
        console.log(`🔄 MyProfile: Updating existing ${type} document record:`, existingDoc.id);
        const updatePayload = {
          name: file.name,
          file_path: filePath,
          status: extractionResult.success ? 'processed' : 'uploaded',
          extracted_content: cleanedText || null,
          text_extraction_status: extractionResult.success ? 'extracted' : 'failed',
          extraction_method: extractionMethod,
          extraction_error_message: extractionResult.success ? null : (extractionResult.error || 'Text extraction failed'),
          extraction_metadata: {
            characterCount: cleanedText?.length || 0,
            method: extractionMethod,
            timestamp: new Date().toISOString()
          },
          updated_at: new Date().toISOString()
        };
        console.log(`📝 MyProfile: Update payload for ${type}:`, updatePayload);
        
        const updateResult = await supabase
          .from('documents')
          .update(updatePayload)
          .eq('id', existingDoc.id)
          .select()
          .maybeSingle();
        
        docData = updateResult.data;
        docError = updateResult.error;
        console.log(`📊 MyProfile: Update result for ${type}:`, docError ? 'ERROR' : 'SUCCESS');
        if (docError) {
          console.error(`❌ MyProfile: Database update error for ${type}:`, docError);
        } else {
          console.log(`✅ MyProfile: Database update successful for ${type}:`, docData);
        }
      } else {
        // Create new document record
        console.log(`🆕 MyProfile: Creating new ${type} document record`);
        const insertPayload = {
          user_id: user.id,
          name: file.name,
          type: type,
          file_path: filePath,
          status: extractionResult.success ? 'processed' : 'uploaded',
          extracted_content: cleanedText || null,
          text_extraction_status: extractionResult.success ? 'extracted' : 'failed',
          extraction_method: extractionMethod,
          extraction_error_message: extractionResult.success ? null : (extractionResult.error || 'Text extraction failed'),
          extraction_metadata: {
            characterCount: cleanedText?.length || 0,
            method: extractionMethod,
            timestamp: new Date().toISOString()
          },
          ocr_used: extractionMethod === 'ocr',
          ocr_confidence: extractionResult.ocrConfidence || null,
          is_image_based: extractionResult.isImageBased || false
        };
        console.log(`📝 MyProfile: Insert payload for ${type}:`, insertPayload);
        
        const insertResult = await supabase
          .from('documents')
          .insert(insertPayload)
          .select()
          .maybeSingle();
        
        docData = insertResult.data;
        docError = insertResult.error;
        console.log(`📊 MyProfile: Insert result for ${type}:`, docError ? 'ERROR' : 'SUCCESS');
        if (docError) {
          console.error(`❌ MyProfile: Database insert error for ${type}:`, docError);
        } else {
          console.log(`✅ MyProfile: Database insert successful for ${type}:`, docData);
        }
      }

      if (docError) {
        console.error(`❌ MyProfile: ${type} database record error:`, docError);
        throw new Error(`Failed to save document record: ${docError.message}`);
      }

      console.log(`✅ MyProfile: ${type} document record ${existingDoc ? 'updated' : 'created'}:`, docData.id);
      setUploadProgress({ [type]: 100 });

      // NEW: Use extraction-worker job queue system for better reliability
      console.log(`🚀 MyProfile: Starting extraction-worker job queue for ${type}...`);
      console.log(`📊 MyProfile: Extraction result success: ${extractionResult.success}`);
      console.log(`📊 MyProfile: Extraction method: ${extractionMethod}`);

      // Use external extraction service if:
      // 1. Client-side extraction failed completely
      // 2. Extraction method was 'fallback' (indicates poor quality)
      // 3. Text is too short (< 500 chars for resume/dream sheet)
      const shouldUseExternalService =
        !extractionResult.success ||
        extractionMethod === 'fallback_text_extraction' ||
        cleanedText.length < 500;

      if (shouldUseExternalService) {
        console.log(`🔄 MyProfile: Using external extraction service (pdf.co) for ${type}...`);
        console.log(`📊 Reason: success=${extractionResult.success}, method=${extractionMethod}, length=${cleanedText.length}`);
        setExtractionStatus({ ...extractionStatus, [type]: 'Processing with PDF.co Professional Service...' });

        try {
          // Use pdf.co external service for better extraction
          const externalResult = await extractWithPdfCo(docData.id, user.id);

          if (!externalResult.success) {
            throw new Error(externalResult.error || 'External extraction failed');
          }

          console.log(`✅ MyProfile: External extraction completed for ${type}`);
          console.log(`📊 Characters: ${externalResult.characterCount}, Quality: ${externalResult.qualityScore}/100`);

          toast.success(
            `Resume uploaded and processed successfully!\n` +
            `${externalResult.characterCount.toLocaleString()} characters extracted\n` +
            `Quality: ${externalResult.qualityScore}/100 (PDF.co Professional Service)`
          );

          setExtractionStatus({ ...extractionStatus, [type]: 'Completed' });

        } catch (extractionError: any) {
          console.error(`❌ MyProfile: External extraction failed for ${type}:`, extractionError);

          // Fallback to showing upload success with extraction pending
          toast.error(
            `${type} uploaded but text extraction failed.\n\n` +
            `Error: ${extractionError.message}\n\n` +
            `Your file is saved. Please try re-uploading or contact support if this persists.`
          );
          setExtractionStatus({ ...extractionStatus, [type]: 'Error' });
        }
      } else {
        // Client-side extraction was successful - use it directly
        console.log(`✅ MyProfile: Client-side extraction successful for ${type}, ${cleanedText.length} characters`);

        // Calculate a basic quality score based on content
        const qualityScore = cleanedText.length >= 1000 ? 85 : cleanedText.length >= 500 ? 75 : 65;
        const confidenceLevel = qualityScore >= 80 ? 'high' : qualityScore >= 70 ? 'medium' : 'low';

        toast.success(
          `Resume uploaded and processed successfully!\n` +
          `${cleanedText.length.toLocaleString()} characters extracted\n` +
          `Quality: ${qualityScore}/100 (${confidenceLevel} confidence)`
        );
        setExtractionStatus({ ...extractionStatus, [type]: 'Completed' });
      }

      // Refresh documents list after extraction
      console.log(`🔄 MyProfile: Refreshing documents list after ${type} upload...`);
      await fetchDocuments();

      // Clear progress after documents are refreshed
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[type];
        return newProgress;
      });

      console.log(`🎉 MyProfile: ${type} upload process completed`);

      /*
      // OLD EXTRACTION WORKER CODE - DISABLED
      if (false) {
        try {
          const { success: workerSuccess, jobId, error: workerError } = await startExtractionWorker(
            docData.id,
            user.id,
            {
              ocr_used: extractionResult.extractionMethod === 'ocr',
              ocr_confidence: extractionResult.ocrConfidence,
              is_image_based: extractionResult.isImageBased,
              extraction_method: extractionMethod
            }
          );

          if (!workerSuccess || !jobId) {
            throw new Error(workerError || 'Failed to start extraction worker');
          }

          console.log(`✅ MyProfile: Extraction worker started for ${type}, job ID: ${jobId}`);
          setExtractionStatus({ ...extractionStatus, [type]: 'Processing...' });

          // Poll the job for completion with progress updates
          const { job: completedJob, error: pollError } = await pollExtractionJob(
            jobId,
            (job) => {
              // Update UI with real-time progress
              console.log(`📊 MyProfile: Job progress for ${type}:`, job.status);
              setExtractionJob({ ...extractionJob, [type]: job });

              if (job.status === 'processing') {
                setExtractionStatus({ ...extractionStatus, [type]: 'Extracting text...' });
              } else if (job.status === 'retrying') {
                setExtractionStatus({ ...extractionStatus, [type]: `Retrying (attempt ${job.attempt_count + 1})...` });
              }
            },
            30, // Max 30 attempts
            2000 // Check every 2 seconds
          );

          if (pollError) {
            console.error(`❌ MyProfile: Job polling error for ${type}:`, pollError);
            throw pollError;
          }

          if (!completedJob) {
            throw new Error('Job not found after polling');
          }

          // Handle completed job
          if (completedJob.status === 'completed') {
            console.log(`✅ MyProfile: Extraction completed successfully for ${type}:`, {
              quality_score: completedJob.quality_score,
              confidence_level: completedJob.confidence_level,
              processing_time: formatProcessingTime(completedJob.processing_time_ms)
            });

            const qualityScore = completedJob.quality_score || 0;
            const confidenceLevel = completedJob.confidence_level || 'unknown';
            const ocrUsed = completedJob.ocr_used || false;
            const ocrConfidence = completedJob.ocr_confidence || 0;
            const isImageBased = completedJob.is_image_based || false;

            // Build OCR notification message
            let ocrMessage = '';
            if (ocrUsed) {
              ocrMessage = `\n\n📷 OCR was used (scanned document detected)\nOCR Confidence: ${ocrConfidence.toFixed(1)}%`;
            } else if (isImageBased) {
              ocrMessage = '\n\n📷 Image-based document detected';
            }

            // Show success with quality information
            if (qualityScore >= 80) {
              toast.success(
                `${type} uploaded successfully!\n` +
                `Quality: ${qualityScore}/100 (${confidenceLevel} confidence)\n` +
                `${getConfidenceMessage(confidenceLevel)}` +
                ocrMessage
              );
            } else if (qualityScore >= 60) {
              toast.success(
                `${type} uploaded with moderate quality\n` +
                `Quality: ${qualityScore}/100 (${confidenceLevel} confidence)\n` +
                `Consider re-uploading for better results.` +
                ocrMessage
              );
            } else {
              toast.error(
                `${type} uploaded but extraction quality is low\n` +
                `Quality: ${qualityScore}/100 (${confidenceLevel} confidence)\n` +
                (ocrUsed
                  ? 'OCR extraction produced low-quality results. Try scanning at higher resolution.'
                  : 'Please re-export your resume as a text-based PDF for better results.') +
                ocrMessage
              );
            }

            setExtractionStatus({ ...extractionStatus, [type]: 'Completed' });
          } else if (completedJob.status === 'failed') {
            console.error(`❌ MyProfile: Extraction failed for ${type}:`, {
              error_type: completedJob.error_type,
              error_message: completedJob.error_message,
              attempt_count: completedJob.attempt_count
            });

            // Get user-friendly remediation steps
            const remediationSteps = getErrorRemediation(completedJob.error_type);
            const fullMessage =
              `${type} uploaded but text extraction failed after ${completedJob.attempt_count} attempts.\n\n` +
              `Error: ${completedJob.error_message}\n\n` +
              `Next steps:\n${remediationSteps.map((step, i) => `${i + 1}. ${step}`).join('\n')}`;

            toast.error(fullMessage);
            setExtractionStatus({ ...extractionStatus, [type]: 'Failed' });
          } else {
            // Job still processing or unknown status
            console.warn(`⚠️ MyProfile: Unexpected job status for ${type}:`, completedJob.status);
            toast.info(
              `${type} uploaded and queued for processing.\n` +
              `Status: ${completedJob.status}\n` +
              `You'll be notified when extraction completes.`
            );
            setExtractionStatus({ ...extractionStatus, [type]: completedJob.status });
          }
        } catch (extractionError: any) {
          console.error(`❌ MyProfile: Extraction worker error for ${type}:`, extractionError);

          // Fallback to showing upload success with extraction pending
          toast.error(
            `${type} uploaded but extraction failed to start.\n\n` +
            `Error: ${extractionError.message}\n\n` +
            `Your file is saved. Try refreshing the page or contact support.`
          );
          setExtractionStatus({ ...extractionStatus, [type]: 'Error' });
        }
      }
      */
      // END OF OLD EXTRACTION WORKER CODE

    } catch (error: any) {
      console.error(`❌ MyProfile: ${type} upload failed:`, error);
      console.error(`❌ MyProfile: Full error object for ${type}:`, {
        message: error.message,
        stack: error.stack,
        name: error.name,
        cause: error.cause
      });
      
      // Provide more specific error messages
      let errorMessage = error.message || `Failed to upload ${type}`;
      
      if (error.message?.includes('storage')) {
        errorMessage = `Storage error: ${error.message}. Please try again or contact support.`;
      } else if (error.message?.includes('database')) {
        errorMessage = `Database error: ${error.message}. The file was uploaded but couldn't be saved to your profile.`;
      } else if (error.message?.includes('network')) {
        errorMessage = `Network error: Please check your connection and try again.`;
      }
      
      toast.error(errorMessage);
      console.error(`❌ MyProfile: ${type} upload failed:`, error?.message || 'Unknown error');
    } finally {
      console.log(`🏁 MyProfile: Upload process finished for ${type}, setting isUploading to false`);
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    const documentToDelete = documents.find(doc => doc.id === documentId);
    const documentType = documentToDelete?.type || 'document';
    const documentName = documentToDelete?.name || 'this document';
    
    if (!confirm(`Are you sure you want to delete "${documentName}"? This action cannot be undone.`)) return;

    try {
      console.log('🗑️ MyProfile: Deleting document:', documentId);
      
      // Get document details before deletion for cleanup
      const { data: docToDelete, error: fetchError } = await supabase
        .from('documents')
        .select('file_path')
        .eq('id', documentId)
        .maybeSingle();
      
      if (fetchError) {
        console.warn('⚠️ MyProfile: Could not fetch document for cleanup:', fetchError.message);
      }
      
      // Delete from database first
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      // Try to delete file from storage (cleanup)
      if (docToDelete?.file_path) {
        try {
          console.log('🗑️ MyProfile: Cleaning up storage file:', docToDelete.file_path);
          const { error: storageDeleteError } = await supabase.storage
            .from('documents')
            .remove([docToDelete.file_path]);
          
          if (storageDeleteError) {
            console.warn('⚠️ MyProfile: Storage cleanup failed:', storageDeleteError.message);
            // Don't fail the operation for storage cleanup issues
          } else {
            console.log('✅ MyProfile: Storage file cleaned up successfully');
          }
        } catch (storageException) {
          console.warn('⚠️ MyProfile: Storage cleanup exception:', storageException);
          // Don't fail the operation for storage cleanup issues
        }
      }

      console.log('✅ MyProfile: Document deleted successfully');
      toast.success('Document deleted successfully');
      
      // Refresh documents list
      await fetchDocuments();
    } catch (error: any) {
      console.error('❌ MyProfile: Error deleting document:', error);
      toast.error(`Failed to delete ${documentType}: ${error.message}`);
    }
  };

  const getDocumentStatusIcon = (doc: Document) => {
    const { status, text_extraction_status } = doc;

    if (status === 'processed' && text_extraction_status === 'extracted') {
      return <CheckCircle className="h-4 w-4 text-green-600" title="Extraction complete" />;
    }

    if (status === 'processing' || text_extraction_status === 'pending') {
      return <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-r-transparent" title="Processing..." />;
    }

    if (status === 'error' || text_extraction_status === 'failed') {
      return <AlertCircle className="h-4 w-4 text-red-600" title="Extraction failed" />;
    }

    return <AlertCircle className="h-4 w-4 text-yellow-600" title="Unknown status" />;
  };

  const getDocumentPreview = (doc: Document) => {
    if (!doc.extracted_content) {
      return 'No text content available';
    }

    return getCleanTextPreview(doc.extracted_content, 200);
  };

  const getDocumentStatusText = (doc: Document): string => {
    const { status, text_extraction_status } = doc;

    if (status === 'processed' && text_extraction_status === 'extracted') {
      return 'Extraction complete';
    }

    if (status === 'uploaded' && text_extraction_status === 'pending') {
      return 'Uploaded - Extracting text...';
    }

    if (status === 'processing') {
      return 'Processing...';
    }

    if (text_extraction_status === 'failed') {
      return 'Extraction failed';
    }

    if (status === 'error') {
      return 'Error';
    }

    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getExtractionCharCount = (doc: Document): string | null => {
    if (!doc.extracted_content) return null;
    const charCount = doc.extracted_content.length;
    return charCount.toLocaleString();
  };

  const getTimeSinceExtraction = (doc: Document): string | null => {
    if (!doc.extracted_at) return null;

    const extractedDate = new Date(doc.extracted_at);
    const now = new Date();
    const diffMs = now.getTime() - extractedDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (isLoading && !loadingTimeout) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600 text-center">
            {isInitializing ? 'Setting up your profile...' : 'Loading your profile...'}
          </p>
          <p className="text-xs text-gray-500 mt-2">This should only take a moment</p>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Failed to Load Profile</h2>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => {
                setError(null);
                setIsLoading(true);
                setLoadingTimeout(false);
                if (user) {
                  fetchProfile().then(() => fetchDocuments());
                }
              }}
              className="w-full"
            >
              Try Again
            </Button>
            <Button
              onClick={() => window.location.href = '/dashboard'}
              variant="outline"
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
            <AlertCircle className="h-6 w-6 text-yellow-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Not Authenticated</h2>
          <p className="text-gray-600 text-center mb-4">Please log in to view your profile.</p>
          <Button
            onClick={() => window.location.href = '/login'}
            className="w-full"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-2">
            Manage your profile information, upload documents, and complete your BRAG and DREAM sheets.
          </p>
        </div>

        <Tabs selectedIndex={activeTab} onSelect={setActiveTab}>
          <TabList className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-8">
            <Tab className="w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 ui-selected:bg-white ui-selected:shadow ui-not-selected:text-blue-100 ui-not-selected:hover:bg-white/[0.12] ui-not-selected:hover:text-white">
              <User className="h-4 w-4 inline mr-2" />
              Profile Info
            </Tab>
            <Tab className="w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 ui-selected:bg-white ui-selected:shadow ui-not-selected:text-blue-100 ui-not-selected:hover:bg-white/[0.12] ui-not-selected:hover:text-white">
              <FileText className="h-4 w-4 inline mr-2" />
              Documents
            </Tab>
            <Tab className="w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 ui-selected:bg-white ui-selected:shadow ui-not-selected:text-blue-100 ui-not-selected:hover:bg-white/[0.12] ui-not-selected:hover:text-white">
              <Star className="h-4 w-4 inline mr-2" />
              BRAG Sheet
            </Tab>
            <Tab className="w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 ui-selected:bg-white ui-selected:shadow ui-not-selected:text-blue-100 ui-not-selected:hover:bg-white/[0.12] ui-not-selected:hover:text-white">
              <Star className="h-4 w-4 inline mr-2" />
              DREAM Sheet
            </Tab>
          </TabList>

          {/* Profile Info Tab */}
          <TabPanel>
            <div className="bg-white rounded-xl border border-border/40 p-6">
              <h2 className="text-xl font-semibold mb-6">Profile Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => handleFormChange('full_name', e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Experience
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={formData.years_experience}
                    onChange={(e) => handleFormChange('years_experience', e.target.value)}
                    placeholder="e.g., 5"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Job
                  </label>
                  <Input
                    value={formData.current_job}
                    onChange={(e) => handleFormChange('current_job', e.target.value)}
                    placeholder="e.g., Software Engineer, Marketing Manager"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Industry
                  </label>
                  <Input
                    value={formData.current_industry}
                    onChange={(e) => handleFormChange('current_industry', e.target.value)}
                    placeholder="e.g., Technology, Healthcare, Finance"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Role
                  </label>
                  <Input
                    value={formData.preferred_role}
                    onChange={(e) => handleFormChange('preferred_role', e.target.value)}
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Industry
                  </label>
                  <Input
                    value={formData.preferred_industry}
                    onChange={(e) => handleFormChange('preferred_industry', e.target.value)}
                    placeholder="e.g., Technology, Healthcare, Finance"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Compensation Range
                  </label>
                  <select
                    value={formData.total_compensation_range}
                    onChange={(e) => handleFormChange('total_compensation_range', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  >
                    <option value="">Select compensation range...</option>
                    <option value="$50K - $100K">$50K - $100K</option>
                    <option value="$100K - $150K">$100K - $150K</option>
                    <option value="$150K - $200K">$150K - $200K</option>
                    <option value="$200K - $250K">$200K - $250K</option>
                    <option value="$250K - $300K">$250K - $300K</option>
                    <option value="$300K - $350K">$300K - $350K</option>
                    <option value="$350K - $400K">$350K - $400K</option>
                    <option value="$400K - $450K">$400K - $450K</option>
                    <option value="$450K - $500K">$450K - $500K</option>
                    <option value="$500K+">$500K+</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Location
                  </label>
                  <Input
                    value={formData.preferred_location}
                    onChange={(e) => handleFormChange('preferred_location', e.target.value)}
                    placeholder="e.g., San Francisco, CA or Remote"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Work Environment
                  </label>
                  <select
                    value={formData.preferred_work_environment}
                    onChange={(e) => handleFormChange('preferred_work_environment', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  >
                    <option value="">Select work environment...</option>
                    <option value="in_office">In-office</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="remote">Remote</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What best describes your current circumstance and intention in gathering this career intelligence?
                  </label>
                  <select
                    value={formData.primary_motivation}
                    onChange={(e) => handleFormChange('primary_motivation', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  >
                    <option value="">Select your primary motivation...</option>
                    <option value="career_growth">Career Growth</option>
                    <option value="currently_displaced">Currently displaced/looking for a new opportunity</option>
                    <option value="second_act">Looking for my second act</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    This helps us tailor the career intelligence to your specific situation and goals.
                  </p>
                </div>
              </div>

              {showDraftRestored && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-blue-800 font-medium">Draft Restored</p>
                      <p className="text-xs text-blue-700 mt-1">
                        We've restored your unsaved changes. Click "Save Profile" to keep them or continue editing.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to discard your draft changes?')) {
                          clearFormDraft();
                          if (profile) {
                            setFormData({
                              full_name: profile.full_name || '',
                              years_experience: profile.years_experience?.toString() || '',
                              preferred_role: profile.preferred_role || '',
                              preferred_industry: profile.preferred_industry || '',
                              primary_motivation: profile.primary_motivation || '',
                              current_job: profile.current_job || '',
                              current_industry: profile.current_industry || '',
                              total_compensation_range: profile.total_compensation_range || '',
                              preferred_location: profile.preferred_location || '',
                              preferred_work_environment: profile.preferred_work_environment || ''
                            });
                            setHasUnsavedChanges(false);
                          }
                        }
                      }}
                      className="text-blue-600 hover:text-blue-800 text-xs underline"
                    >
                      Discard
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {isAutoSaving && (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary border-r-transparent"></div>
                      Auto-saving...
                    </span>
                  )}
                  {!isAutoSaving && lastSavedAt && (
                    <span>
                      Last saved: {lastSavedAt.toLocaleTimeString()}
                    </span>
                  )}
                  {!isAutoSaving && !lastSavedAt && hasUnsavedChanges && (
                    <span className="text-amber-600">
                      Unsaved changes
                    </span>
                  )}
                </div>
                <Button onClick={handleProfileSave} disabled={isSaving || isAutoSaving}>
                  {isSaving ? 'Saving...' : 'Save Profile'}
                </Button>
              </div>

              {/* Account Management Section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Management</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setShowChangePassword(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Key className="h-4 w-4" />
                    Change Password
                  </Button>
                  <Button
                    onClick={() => setShowDeleteAccount(true)}
                    variant="outline"
                    className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <UserX className="h-4 w-4" />
                    Remove Account
                  </Button>
                </div>
              </div>
            </div>
          </TabPanel>

          {/* Documents Tab */}
          <TabPanel>
            <div className="space-y-6">
              {/* Upload Section */}
              <div className="bg-white rounded-xl border border-border/40 p-6">
                <h2 className="text-xl font-semibold mb-6">Upload Documents</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Resume Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      {documents.some(d => d.type === 'resume') ? 'Replace Resume' : 'Upload Resume'}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {documents.some(d => d.type === 'resume') 
                        ? 'Upload a new resume to replace your current one' 
                        : 'Upload your current resume in PDF format'
                      }
                    </p>
                    
                    {uploadProgress.resume !== undefined ? (
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress.resume}%` }}
                        ></div>
                      </div>
                    ) : null}
                    
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'resume');
                      }}
                      className="hidden"
                      id="resume-upload"
                      disabled={isUploading}
                    />
                    <Button 
                      onClick={() => {
                        const input = document.getElementById('resume-upload') as HTMLInputElement;
                        if (input) input.click();
                      }}
                      disabled={isUploading}
                    >
                      {uploadProgress.resume !== undefined ? 'Uploading...' : 
                       documents.some(d => d.type === 'resume') ? 'Choose New Resume PDF' : 'Choose Resume PDF'
                      }
                    </Button>
                  </div>
                </div>
              </div>

              {/* Documents List */}
              <div className="bg-white rounded-xl border border-border/40 p-6">
                <h2 className="text-xl font-semibold mb-6">Uploaded Documents</h2>
                
                {documents.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No documents uploaded</h3>
                    <p className="text-gray-600">
                      Upload your resume to get started with career analysis.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {documents.map((doc) => (
                      <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                              <h3 className="font-medium text-gray-900">{doc.name}</h3>
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                                {doc.type}
                              </span>
                              <div className="flex items-center gap-1">
                                {getDocumentStatusIcon(doc)}
                                <span className="text-xs text-gray-600">
                                  {getDocumentStatusText(doc)}
                                </span>
                              </div>
                              {getExtractionCharCount(doc) && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                  {getExtractionCharCount(doc)} chars
                                </span>
                              )}
                              {doc.extraction_method && doc.extraction_method === 'pdfco' && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800" title="Extracted with PDF.co Professional Service">
                                  PDF.co
                                </span>
                              )}
                              {/* Quality Score Badge */}
                              {doc.quality_score !== null && doc.quality_score !== undefined && (
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  doc.quality_score >= 80 ? 'bg-green-100 text-green-800' :
                                  doc.quality_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {doc.quality_score >= 80 ? '✓' : doc.quality_score >= 60 ? '⚠' : '✗'} {doc.quality_score}/100
                                </span>
                              )}
                              {/* OCR Badge */}
                              {doc.ocr_used && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                  📷 OCR
                                </span>
                              )}
                            </div>
                            
                            <div className="text-sm text-gray-600 mb-2 space-y-1">
                              <p>
                                {doc.updated_at !== doc.created_at ? 'Updated' : 'Uploaded'}: {new Date(doc.updated_at || doc.created_at).toLocaleDateString()}
                              </p>
                              {doc.extracted_at && (
                                <p className="text-xs text-green-600 font-medium">
                                  Extracted: {getTimeSinceExtraction(doc)}
                                </p>
                              )}
                            </div>
                            
                            {doc.extracted_content && (
                              <div className="bg-gray-50 rounded-md p-3 mt-3">
                                <h4 className="text-xs font-medium text-gray-700 mb-2">Content Preview:</h4>
                                <p className="text-xs text-gray-600 leading-relaxed">
                                  {getDocumentPreview(doc)}
                                </p>
                              </div>
                            )}
                            
                            {doc.extraction_method && (
                              <p className="text-xs text-gray-500 mt-2">
                                Extraction method: {doc.extraction_method}
                              </p>
                            )}

                            {/* Extraction Error Message Component */}
                            {(doc.status === 'error' || doc.text_extraction_status === 'failed') && doc.extraction_error_message && (
                              <div className="mt-3">
                                <ExtractionErrorMessage
                                  errorType={extractionJob[doc.type]?.error_type}
                                  errorMessage={doc.extraction_error_message}
                                  attemptCount={extractionJob[doc.type]?.attempt_count || 1}
                                  documentType={doc.type}
                                  onReupload={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = '.pdf';
                                    input.onchange = (e) => {
                                      const file = (e.target as HTMLInputElement).files?.[0];
                                      if (file) {
                                        handleFileUpload(file, doc.type as 'resume');
                                      }
                                    };
                                    input.click();
                                  }}
                                  onViewRequirements={() => {
                                    // Scroll to PDF requirements section or open modal
                                    const requirementsSection = document.querySelector('[data-section="pdf-requirements"]');
                                    requirementsSection?.scrollIntoView({ behavior: 'smooth' });
                                  }}
                                />
                              </div>
                            )}

                            {/* Quality Warning Component */}
                            {doc.quality_score !== null && doc.quality_score !== undefined && (
                              <div className="mt-3">
                                <QualityWarning
                                  qualityScore={doc.quality_score}
                                  confidenceLevel={doc.confidence_level || 'medium'}
                                  qualityIssues={doc.quality_issues || []}
                                  documentName={doc.type}
                                  onReupload={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = '.pdf';
                                    input.onchange = (e) => {
                                      const file = (e.target as HTMLInputElement).files?.[0];
                                      if (file) {
                                        handleFileUpload(file, doc.type as 'resume');
                                      }
                                    };
                                    input.click();
                                  }}
                                  onAccept={async () => {
                                    if (doc.status === 'quality_review') {
                                      await supabase
                                        .from('documents')
                                        .update({
                                          status: 'processed',
                                          quality_check_passed: true
                                        })
                                        .eq('id', doc.id);
                                      toast.success('Document accepted with current quality');
                                      await fetchDocuments();
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            {doc.extracted_content && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPreviewModal({ isOpen: true, document: doc })}
                                className="text-green-600 hover:text-green-800"
                                title="Preview extracted text"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                // Create a file input to trigger replacement
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = '.pdf';
                                input.onchange = (e) => {
                                  const file = (e.target as HTMLInputElement).files?.[0];
                                  if (file) {
                                    handleFileUpload(file, doc.type as 'resume');
                                  }
                                };
                                input.click();
                              }}
                              className="text-blue-600 hover:text-blue-800"
                              title={`Replace ${doc.type}`}
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="text-red-600 hover:text-red-800"
                              title={`Delete ${doc.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabPanel>

          {/* BRAG Sheet Tab */}
          <TabPanel>
            <BragSheet onComplete={() => {
              toast.success('BRAG Sheet completed!');
              // Optionally refresh data or navigate
            }} />
          </TabPanel>

          {/* DREAM Sheet Tab */}
          <TabPanel>
            <DreamSheet onComplete={() => {
              toast.success('DREAM Sheet completed!');
              // Optionally refresh data or navigate
            }} />
          </TabPanel>
        </Tabs>
      </div>

      {/* Extracted Text Preview Modal */}
      {previewModal.document && (
        <ExtractedTextPreviewModal
          isOpen={previewModal.isOpen}
          onClose={() => setPreviewModal({ isOpen: false, document: null })}
          documentName={previewModal.document.name}
          extractedText={(previewModal.document as any).extracted_text || previewModal.document.extracted_content || ''}
          extractionMethod={
            previewModal.document.extraction_metadata?.method ||
            previewModal.document.extraction_method ||
            undefined
          }
          qualityScore={
            previewModal.document.extraction_metadata?.quality_score ||
            previewModal.document.quality_score ||
            undefined
          }
          ocrUsed={previewModal.document.ocr_used}
          ocrConfidence={previewModal.document.ocr_confidence || undefined}
        />
      )}
    </div>
  );
};

export default MyProfile;